import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Card, Row, Col, Select, Table, Segmented, Typography, Tooltip } from 'antd';
import { EyeOutlined, InfoCircleOutlined } from '@ant-design/icons';
import * as echarts from 'echarts';
import { setRolePreset } from '../../../utils/role';

const { Title, Text } = Typography;

// TODO: 联调时替换为接口数据
const MOCK_KPI = {
    appCount: 5,            // App 总数
    orderTotal: 3060,       // 订单总量
    validOrders: 2935,      // 有效订单数
    gmvCny: 276200,         // GMV(¥) = 营收总额 + 退款金额
    gmvUsd: 32400,          // GMV($)
    revenueCny: 269000,     // 营收总额(¥)
    revenueUsd: 31500,      // 营收总额($)
    refundAmount: 7200,     // 退款金额(¥)
    refundAmountUsd: 900,   // 退款金额($)
    refundOrders: 125,      // 退款订单数
};

// TODO: 联调时替换为接口数据 —— 订单/营收（月粒度，近12个月）
const MOCK_ORDER_MONTHLY = [
    { month: '2025-09', orderCnt: 320, refundCnt: 12, revenueCny: 28000, revenueUsd: 3200 },
    { month: '2025-10', orderCnt: 360, refundCnt: 14, revenueCny: 31000, revenueUsd: 3600 },
    { month: '2025-11', orderCnt: 390, refundCnt: 15, revenueCny: 34000, revenueUsd: 3900 },
    { month: '2025-12', orderCnt: 420, refundCnt: 16, revenueCny: 37000, revenueUsd: 4300 },
    { month: '2026-01', orderCnt: 450, refundCnt: 18, revenueCny: 40000, revenueUsd: 4600 },
    { month: '2026-02', orderCnt: 470, refundCnt: 17, revenueCny: 42000, revenueUsd: 4800 },
    { month: '2026-03', orderCnt: 320, refundCnt: 12, revenueCny: 28000, revenueUsd: 3200 },
    { month: '2026-04', orderCnt: 420, refundCnt: 16, revenueCny: 36000, revenueUsd: 4300 },
    { month: '2026-05', orderCnt: 510, refundCnt: 20, revenueCny: 45000, revenueUsd: 5200 },
    { month: '2026-06', orderCnt: 580, refundCnt: 24, revenueCny: 52000, revenueUsd: 6100 },
    { month: '2026-07', orderCnt: 650, refundCnt: 28, revenueCny: 60000, revenueUsd: 6800 },
    { month: '2026-08', orderCnt: 700, refundCnt: 32, revenueCny: 64000, revenueUsd: 7300 },
];

// TODO: 联调时替换为接口数据 —— 订单/营收（日粒度，近30天）
const MOCK_ORDER_DAILY = (() => {
    const arr = [];
    const base = new Date('2026-08-19');
    const orderSeed = [22, 18, 20, 25, 19, 28, 23, 30, 26, 21, 33, 27, 24, 29, 26, 34, 28, 23, 32, 36, 30, 25, 35, 38, 32, 27, 36, 40, 34, 30];
    for (let i = 29; i >= 0; i--) {
        const d = new Date(base);
        d.setDate(base.getDate() - i);
        const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        const order = orderSeed[29 - i];
        arr.push({
            date: dateStr,
            orderCnt: order,
            refundCnt: Math.round(order * 0.04),
            revenueCny: order * 85,
            revenueUsd: order * 10,
        });
    }
    return arr;
})();

const timeRangeOptions = [
    { value: '7d', label: '近7天' },
    { value: '30d', label: '近30天' },
    { value: 'month', label: '本月' },
    { value: '3m', label: '近3个月' },
    { value: '6m', label: '近6个月' },
    { value: '1y', label: '近一年' },
];

// 根据时间范围返回订单/营收趋势数据（日粒度：近7天/近30天/本月；月粒度：近3月/6月/一年）
const getOrderTrendData = (range) => {
    if (range === '7d' || range === '30d' || range === 'month') {
        let daily = MOCK_ORDER_DAILY;
        if (range === '7d') daily = daily.slice(-7);
        if (range === 'month') daily = daily.filter((d) => d.date.startsWith('2026-08'));
        return {
            labels: daily.map((d) => d.date.slice(5)),
            orderCnt: daily.map((d) => d.orderCnt),
            refundCnt: daily.map((d) => d.refundCnt),
            revenueCny: daily.map((d) => d.revenueCny),
            revenueUsd: daily.map((d) => d.revenueUsd),
            refundAmountCny: daily.map((d) => d.refundCnt * 200),
            refundAmountUsd: daily.map((d) => d.refundCnt * 25),
        };
    }
    const months = { '3m': 3, '6m': 6, '1y': 12 }[range];
    const monthly = MOCK_ORDER_MONTHLY.slice(-months);
    return {
        labels: monthly.map((m) => m.month.slice(2)),
        orderCnt: monthly.map((m) => m.orderCnt),
        refundCnt: monthly.map((m) => m.refundCnt),
        revenueCny: monthly.map((m) => m.revenueCny),
        revenueUsd: monthly.map((m) => m.revenueUsd),
        refundAmountCny: monthly.map((m) => m.refundCnt * 200),
        refundAmountUsd: monthly.map((m) => m.refundCnt * 25),
    };
};

// TODO: 联调时替换为接口数据 —— 客户价值排名（按设备归属企业订单量）
const MOCK_CUST_RANK = [
    { key: '1', rank: 1, entName: 'test-制造商一有限公司', entId: 'test-ent-mfg-a', orderCount: 1200, revenueCny: 98000.00, revenueUsd: 12000.00, deviceCount: 8000 },
    { key: '2', rank: 2, entName: 'test-制造商二科技有限公司', entId: 'test-ent-mfg-b', orderCount: 850, revenueCny: 72000.00, revenueUsd: 9000.00, deviceCount: 5200 },
    { key: '3', rank: 3, entName: 'test-制造商三物联网公司', entId: 'test-ent-mfg-c', orderCount: 540, revenueCny: 45000.00, revenueUsd: 5600.00, deviceCount: 3600 },
    { key: '4', rank: 4, entName: 'test-制造商四有限公司', entId: 'test-ent-mfg-d', orderCount: 320, revenueCny: 26000.00, revenueUsd: 3200.00, deviceCount: 2100 },
    { key: '5', rank: 5, entName: 'test-制造商五科技有限公司', entId: 'test-ent-mfg-e', orderCount: 150, revenueCny: 12000.00, revenueUsd: 1500.00, deviceCount: 900 },
];

// TODO: 联调时替换为接口数据 —— 明星产品
const MOCK_STAR_PRODUCTS = [
    { key: '1', name: 'test-自研高清摄像机', id: 'test-prod-001', category: '摄像机', entName: 'test-制造商一有限公司', entId: 'test-ent-mfg-a', deviceCount: 12000, orderCount: 800, revenueCny: 60000.00, revenueUsd: 8000.00 },
    { key: '2', name: 'test-4G低功耗摄像机', id: 'test-prod-003', category: '摄像机', entName: 'test-制造商二科技有限公司', entId: 'test-ent-mfg-b', deviceCount: 7200, orderCount: 500, revenueCny: 40000.00, revenueUsd: 5000.00 },
    { key: '3', name: 'test-派生云台摄像机A款', id: 'test-prod-002', category: '摄像机', entName: 'test-制造商一有限公司', entId: 'test-ent-mfg-a', deviceCount: 6500, orderCount: 450, revenueCny: 36000.00, revenueUsd: 4200.00 },
    { key: '4', name: 'test-智能门铃产品', id: 'test-prod-004', category: '摄像机', entName: 'test-制造商二科技有限公司', entId: 'test-ent-mfg-b', deviceCount: 3200, orderCount: 300, revenueCny: 22000.00, revenueUsd: 3000.00 },
    { key: '5', name: 'test-户外枪机产品', id: 'test-prod-005', category: '摄像机', entName: 'test-制造商一有限公司', entId: 'test-ent-mfg-a', deviceCount: 1800, orderCount: 120, revenueCny: 9000.00, revenueUsd: 1200.00 },
];

// TODO: 联调时替换为接口数据 —— 首复购
const MOCK_REPURCHASE = {
    firstRate: 62.5,        // 首购率 %
    firstCnt: 1835,         // 首购订单数
    firstCny: 165000,       // 首购金额(¥)
    firstUsd: 19000,        // 首购金额($)
    repurchaseRate: 37.5,   // 复购率 %
    repurchaseCnt: 1100,    // 复购订单数
    repurchaseCny: 104000,  // 复购金额(¥)
    repurchaseUsd: 12500,   // 复购金额($)
};

// TODO: 联调时替换为接口数据 —— 畅销套餐
const MOCK_HOT_PACKAGES = [
    { key: '1', id: 'test-pkg-001', name: 'test-全天云存套餐', overseasName: 'Cloud Storage 24/7', domesticSales: 1200, domesticAmount: 118800, overseasSales: 350, overseasAmount: 52000 },
    { key: '2', id: 'test-pkg-002', name: 'test-事件云存套餐', overseasName: 'Event Recording', domesticSales: 900, domesticAmount: 44100, overseasSales: 280, overseasAmount: 37800 },
    { key: '3', id: 'test-pkg-003', name: 'test-人脸识别套餐', overseasName: 'Face Recognition', domesticSales: 620, domesticAmount: 86800, overseasSales: 210, overseasAmount: 35700 },
    { key: '4', id: 'test-pkg-004', name: 'test-逗留提醒套餐', overseasName: 'Loitering Alert', domesticSales: 480, domesticAmount: 23520, overseasSales: 150, overseasAmount: 14850 },
    { key: '5', id: 'test-pkg-005', name: 'test-语音助手套餐', overseasName: 'Voice Assistant', domesticSales: 350, domesticAmount: 34650, overseasSales: 120, overseasAmount: 13200 },
];

const RANK_BADGE = {
    1: { text: 'Top1', color: '#f59e0b' },
    2: { text: 'Top2', color: '#9ca3af' },
    3: { text: 'Top3', color: '#d97706' },
};

// 排名方式选项
const rankOptions = [
    { value: 'orderCount', label: '按有效单量' },
    { value: 'deviceCount', label: '按设备规模' },
    { value: 'revenueCny', label: '按人民币营收' },
    { value: 'revenueUsd', label: '按美元营收' },
];

// 退款口径选项（首复购 / 畅销套餐共用）
const refundModeOptions = [
    { value: 'exclude', label: '剔除退款' },
    { value: 'include', label: '包含退款' },
];

// 表头不换行
const tableHeaderNowrap = {
    header: {
        cell: (props) => <th {...props} style={{ ...props.style, whiteSpace: 'nowrap' }} />,
    },
};

const OrderDashboard = () => {
    const navigate = useNavigate();
    // 企业视角：登录企业名下 App 数据
    setRolePreset('enterprise', 'app');

    const [timeRange, setTimeRange] = useState('7d');
    const [region, setRegion] = useState('domestic'); // 畅销套餐：domestic 国内 / overseas 海外
    const [custSortKey, setCustSortKey] = useState('orderCount'); // 客户价值排名
    const [starSortKey, setStarSortKey] = useState('orderCount'); // 明星产品
    const [repurchaseRefundMode, setRepurchaseRefundMode] = useState('exclude'); // 首复购退款口径：exclude 剔除退款 / include 包含退款
    const [hotPackageRefundMode, setHotPackageRefundMode] = useState('exclude'); // 畅销套餐退款口径：exclude 剔除退款 / include 包含退款

    const trend1Ref = useRef(null);
    const trend2Ref = useRef(null);
    const trend1Instance = useRef(null);
    const trend2Instance = useRef(null);
    const repurchaseRef1 = useRef(null);
    const repurchaseRef2 = useRef(null);
    const repurchaseInstance1 = useRef(null);
    const repurchaseInstance2 = useRef(null);

    // 图表1：订单量趋势（订单量 + 退款订单，依赖时间范围）
    useEffect(() => {
        const { labels, orderCnt, refundCnt } = getOrderTrendData(timeRange);
        if (!trend1Ref.current) return;
        if (!trend1Instance.current) {
            trend1Instance.current = echarts.init(trend1Ref.current);
            trend1Instance.current.setOption({
                legend: { data: ['订单量', '退款订单'], top: 10, selectedMode: true },
                tooltip: { trigger: 'axis', axisPointer: { type: 'cross' } },
                grid: { top: 60, left: 60, right: 40, bottom: 40 },
                xAxis: { type: 'category', data: labels, axisLine: { lineStyle: { color: '#999' } } },
                yAxis: { type: 'value', name: '订单数', min: 0, minInterval: 1, axisLabel: { formatter: (v) => Math.floor(v) } },
                series: [
                    { name: '订单量', type: 'bar', data: orderCnt, barWidth: 24, itemStyle: { color: '#5470c6' } },
                    { name: '退款订单', type: 'line', data: refundCnt, smooth: true, lineStyle: { color: '#ee6666', width: 2.5 }, itemStyle: { color: '#ee6666' }, symbol: 'circle', symbolSize: 6 },
                ],
            });
        } else {
            trend1Instance.current.setOption({
                xAxis: { data: labels },
                series: [
                    { name: '订单量', data: orderCnt },
                    { name: '退款订单', data: refundCnt },
                ],
            });
        }
    }, [timeRange]);

    // 图表2：营收趋势（按币种双柱堆叠：有效营收 + 退款金额，依赖时间范围）
    useEffect(() => {
        const d = getOrderTrendData(timeRange);
        if (!trend2Ref.current) return;
        const tooltip = {
            trigger: 'axis',
            axisPointer: { type: 'shadow' },
            formatter: (params) => {
                const idx = params[0].dataIndex;
                const label = params[0].axisValue;
                return [
                    label,
                    `有效营收(¥)：¥${d.revenueCny[idx].toLocaleString('zh-CN')}`,
                    `退款金额(¥)：¥${d.refundAmountCny[idx].toLocaleString('zh-CN')}`,
                    `有效营收($)：$${d.revenueUsd[idx].toLocaleString('zh-CN')}`,
                    `退款金额($)：$${d.refundAmountUsd[idx].toLocaleString('zh-CN')}`,
                ].join('<br/>');
            },
        };
        if (!trend2Instance.current) {
            trend2Instance.current = echarts.init(trend2Ref.current);
            trend2Instance.current.setOption({
                legend: { data: ['有效营收(¥)', '退款金额(¥)', '有效营收($)', '退款金额($)'], top: 10, selectedMode: true },
                tooltip,
                grid: { top: 60, left: 70, right: 40, bottom: 40 },
                xAxis: { type: 'category', data: d.labels, axisLine: { lineStyle: { color: '#999' } } },
                yAxis: { type: 'value', name: '金额', min: 0, axisLabel: { formatter: (v) => Math.floor(v) } },
                series: [
                    { name: '有效营收(¥)', type: 'bar', stack: 'cny', data: d.revenueCny, barWidth: 16, itemStyle: { color: '#91cc75' } },
                    { name: '退款金额(¥)', type: 'bar', stack: 'cny', data: d.refundAmountCny, itemStyle: { color: '#ee6666' } },
                    { name: '有效营收($)', type: 'bar', stack: 'usd', data: d.revenueUsd, barWidth: 16, itemStyle: { color: '#5470c6' } },
                    { name: '退款金额($)', type: 'bar', stack: 'usd', data: d.refundAmountUsd, itemStyle: { color: '#fc8452' } },
                ],
            });
        } else {
            trend2Instance.current.setOption({
                tooltip,
                xAxis: { data: d.labels },
                series: [
                    { name: '有效营收(¥)', data: d.revenueCny },
                    { name: '退款金额(¥)', data: d.refundAmountCny },
                    { name: '有效营收($)', data: d.revenueUsd },
                    { name: '退款金额($)', data: d.refundAmountUsd },
                ],
            });
        }
    }, [timeRange]);

    // 首复购环形图（首购率 / 复购率）
    useEffect(() => {
        const renderRing = (ref, instance, rate, color, title) => {
            if (!ref.current) return;
            instance.current = echarts.init(ref.current);
            instance.current.setOption({
                series: [{
                    type: 'pie',
                    radius: ['62%', '82%'],
                    avoidLabelOverlap: false,
                    label: { show: false },
                    data: [
                        { value: rate, name: '占比' },
                        { value: Math.round((100 - rate) * 10) / 10, name: '其他' },
                    ],
                    color: [color, '#f0f0f0'],
                    itemStyle: { borderColor: '#fff', borderWidth: 2 },
                }],
                graphic: [
                    { type: 'text', left: 'center', top: '34%', style: { text: `${rate}%`, textAlign: 'center', fontSize: 20, fontWeight: 'bold', fill: color } },
                    { type: 'text', left: 'center', top: '56%', style: { text: title, textAlign: 'center', fontSize: 12, fill: '#606266' } },
                ],
            });
        };
        renderRing(repurchaseRef1, repurchaseInstance1, MOCK_REPURCHASE.firstRate, '#1890ff', '首购率');
        renderRing(repurchaseRef2, repurchaseInstance2, MOCK_REPURCHASE.repurchaseRate, '#52c41a', '复购率');
    }, []);

    useEffect(() => {
        const handleResize = () => {
            trend1Instance.current?.resize();
            trend2Instance.current?.resize();
            repurchaseInstance1.current?.resize();
            repurchaseInstance2.current?.resize();
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const kpiCards = [
        { title: '订单总量', value: MOCK_KPI.orderTotal, suffix: '笔', color: '#52c41a', tip: '全部增值服务订单数（含退款）' },
        { title: 'GMV', cny: MOCK_KPI.gmvCny, usd: MOCK_KPI.gmvUsd, color: '#1890ff', tip: '总交易额（有效营收 + 退款金额，人民币 / 美元）', multiCurrency: true },
        { title: '营收总额', cny: MOCK_KPI.revenueCny, usd: MOCK_KPI.revenueUsd, color: '#722ed1', tip: '有效订单营收（人民币 / 美元）', multiCurrency: true },
        { title: '退款订单数', value: MOCK_KPI.refundOrders, suffix: '笔', color: '#fa8c16', tip: '已退款订单数量' },
        { title: '退款金额', cny: MOCK_KPI.refundAmount, usd: MOCK_KPI.refundAmountUsd, color: '#eb2f96', tip: '已退款订单的退款金额', multiCurrency: true },
    ];

    const custColumns = [
        {
            title: '排名', dataIndex: 'rank', width: 70, align: 'center',
            render: (v) => RANK_BADGE[v]
                ? <span style={{ display: 'inline-block', minWidth: 40, padding: '2px 8px', borderRadius: 10, background: RANK_BADGE[v].color, color: '#fff', fontWeight: 600, fontSize: 12 }}>Top{v}</span>
                : <Text strong style={{ color: '#999' }}>{v}</Text>,
        },
        {
            title: '客户企业', key: 'ent',
            render: (_, r) => (<div>
                <div style={{ fontWeight: 500, color: '#1890ff', cursor: 'pointer' }} onClick={() => navigate(`/company/info?id=${r.entId}`)}>{r.entName}</div>
                <Text type="secondary" style={{ fontSize: 12 }}>{r.entId}</Text>
            </div>),
        },
        { title: '有效订单', dataIndex: 'orderCount', align: 'right', render: (v) => <Text strong>{v.toLocaleString()}</Text> },
        { title: '设备规模', dataIndex: 'deviceCount', align: 'right', render: (v) => <Text strong>{v.toLocaleString()}</Text> },
        {
            title: '有效营收', key: 'revenue', align: 'right',
            render: (_, r) => (
                <div>
                    <div style={{ whiteSpace: 'nowrap' }}>¥{r.revenueCny.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</div>
                    <div style={{ whiteSpace: 'nowrap' }}>${r.revenueUsd.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</div>
                </div>
            ),
        },
    ];

    const starColumns = [
        {
            title: '排名', dataIndex: 'rank', width: 70, align: 'center',
            render: (v) => v <= 3
                ? <span style={{ display: 'inline-block', minWidth: 40, padding: '2px 8px', borderRadius: 10, background: ['#f59e0b', '#9ca3af', '#d97706'][v - 1], color: '#fff', fontWeight: 600, fontSize: 12 }}>Top{v}</span>
                : <Text strong style={{ color: '#999' }}>{v}</Text>,
        },
        {
            title: '产品', key: 'product',
            render: (_, r) => (<div>
                <div style={{ fontWeight: 500, color: '#1890ff', cursor: 'pointer' }} onClick={() => navigate(`/product/info?id=${r.id}`)}>{r.name}</div>
                <Text type="secondary" style={{ fontSize: 12 }}>{r.id}</Text>
            </div>),
        },
        {
            title: '产品所属企业', key: 'ent',
            render: (_, r) => (<div>
                <div style={{ fontWeight: 500 }}>{r.entName}</div>
                <Text type="secondary" style={{ fontSize: 12 }}>{r.entId}</Text>
            </div>),
        },
        { title: '有效订单', dataIndex: 'orderCount', align: 'right', render: (v) => <Text strong>{v.toLocaleString()}</Text> },
        { title: '设备规模', dataIndex: 'deviceCount', align: 'right', render: (v) => <Text strong>{v.toLocaleString()}</Text> },
        {
            title: '有效营收', key: 'revenue', align: 'right',
            render: (_, r) => (
                <div>
                    <div style={{ whiteSpace: 'nowrap' }}>¥{r.revenueCny.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</div>
                    <div style={{ whiteSpace: 'nowrap' }}>${r.revenueUsd.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</div>
                </div>
            ),
        },
    ];

    const custData = [...MOCK_CUST_RANK].sort((a, b) => b[custSortKey] - a[custSortKey]).map((r, i) => ({ ...r, rank: i + 1 }));
    const starData = [...MOCK_STAR_PRODUCTS].sort((a, b) => b[starSortKey] - a[starSortKey]).map((r, i) => ({ ...r, rank: i + 1 }));

    const hotPackageData = [...MOCK_HOT_PACKAGES]
        .sort((a, b) => (region === 'domestic' ? b.domesticSales - a.domesticSales : b.overseasSales - a.overseasSales))
        .map((r, i) => ({ ...r, rank: i + 1 }));

    const hotPackageColumns = [
        {
            title: '排名', dataIndex: 'rank', width: 70, align: 'center',
            render: (v) => v <= 3
                ? <span style={{ display: 'inline-block', minWidth: 40, padding: '2px 8px', borderRadius: 10, background: ['#f59e0b', '#9ca3af', '#d97706'][v - 1], color: '#fff', fontWeight: 600, fontSize: 12 }}>Top{v}</span>
                : <Text strong style={{ color: '#999' }}>{v}</Text>,
        },
        {
            title: '套餐名称', key: 'name', width: 200,
            render: (_, r) => (<div>
                <div style={{ fontWeight: 500 }}>{region === 'domestic' ? r.name : r.overseasName}</div>
                <Text type="secondary" style={{ fontSize: 12 }}>{r.id}</Text>
            </div>),
        },
        {
            title: '销量', key: 'sales', width: 90, align: 'right',
            render: (_, r) => <Text strong>{(region === 'domestic' ? r.domesticSales : r.overseasSales).toLocaleString()}</Text>,
        },
        {
            title: '销售金额', key: 'amount', width: 130, align: 'right',
            render: (_, r) => region === 'domestic'
                ? <Text strong style={{ color: '#fa8c16' }}>¥{r.domesticAmount.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</Text>
                : <Text strong style={{ color: '#722ed1' }}>${r.overseasAmount.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</Text>,
        },
    ];

    return (
        <div style={{ padding: 24 }}>
            <div style={{ marginBottom: 16 }}><Title level={4}>App云存收益</Title></div>
            <Alert
                type="warning"
                showIcon
                icon={<EyeOutlined />}
                message="本看板展示贵司 App 的订单经营情况，涵盖订单量、营收、退款、首复购、畅销套餐及客户价值等，统计范围为本企业名下 App 产生的订单。"
                style={{ marginBottom: 16, borderRadius: 8 }}
            />

            {/* ═══════════ 核心指标 ═══════════ */}
            <Card title="核心指标" bordered={false} style={{ marginBottom: 16, borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                <Row gutter={[16, 16]}>
                {kpiCards.map((c) => (
                    <Col flex="1 1 0" key={c.title}>
                        <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)', minHeight: 100 }}>
                            <div style={{ fontSize: 12, color: '#606266', marginBottom: 8 }}>
                                {c.title}
                                <Tooltip title={c.tip}><InfoCircleOutlined style={{ color: '#999', fontSize: 12, marginLeft: 4, cursor: 'help' }} /></Tooltip>
                            </div>
                            {c.multiCurrency ? (
                                <div>
                                    <div style={{ color: c.color, fontWeight: 'bold', fontSize: 18, whiteSpace: 'nowrap' }}>¥{c.cny.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</div>
                                    <div style={{ color: c.color, fontWeight: 'bold', fontSize: 18, whiteSpace: 'nowrap' }}>${c.usd.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</div>
                                </div>
                            ) : (
                                <div style={{ color: c.color, fontWeight: 'bold', fontSize: c.money ? 20 : 22, whiteSpace: 'nowrap', lineHeight: '32px' }}>
                                    {c.money ? `¥${c.value.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}` : `${c.value}${c.suffix}`}
                                </div>
                            )}
                        </Card>
                    </Col>
                ))}
                </Row>
            </Card>

            {/* ═══════════ 订单量趋势 / 首复购 ═══════════ */}
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                <Col span={12}>
                    <Card
                        title="订单量趋势"
                        extra={(
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ color: '#606266', fontSize: 13 }}>时间范围</span>
                                <Select value={timeRange} onChange={setTimeRange} options={timeRangeOptions} style={{ width: 120 }} />
                            </span>
                        )}
                        bordered={false}
                        style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}
                    >
                        <div ref={trend1Ref} style={{ width: '100%', height: 300 }} />
                    </Card>
                </Col>
                <Col span={12}>
                    <Card
                        title={(
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                <span>首复购</span>
                                <Select value={repurchaseRefundMode} onChange={setRepurchaseRefundMode} options={refundModeOptions} style={{ width: 120 }} />
                            </div>
                        )}
                        bordered={false}
                        style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}
                    >
                        <Row gutter={[16, 16]}>
                            <Col span={12}>
                                <div ref={repurchaseRef1} style={{ width: '100%', height: 150 }} />
                                <div style={{ textAlign: 'center', marginTop: 8 }}>
                                    <div style={{ color: '#606266', fontSize: 12 }}>首购订单数</div>
                                    <div style={{ fontWeight: 'bold', fontSize: 18 }}>{MOCK_REPURCHASE.firstCnt.toLocaleString()}</div>
                                    <div style={{ fontSize: 12, whiteSpace: 'nowrap' }}>¥{MOCK_REPURCHASE.firstCny.toLocaleString('zh-CN', { minimumFractionDigits: 2 })} / ${MOCK_REPURCHASE.firstUsd.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</div>
                                </div>
                            </Col>
                            <Col span={12}>
                                <div ref={repurchaseRef2} style={{ width: '100%', height: 150 }} />
                                <div style={{ textAlign: 'center', marginTop: 8 }}>
                                    <div style={{ color: '#606266', fontSize: 12 }}>复购订单数</div>
                                    <div style={{ fontWeight: 'bold', fontSize: 18 }}>{MOCK_REPURCHASE.repurchaseCnt.toLocaleString()}</div>
                                    <div style={{ fontSize: 12, whiteSpace: 'nowrap' }}>¥{MOCK_REPURCHASE.repurchaseCny.toLocaleString('zh-CN', { minimumFractionDigits: 2 })} / ${MOCK_REPURCHASE.repurchaseUsd.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</div>
                                </div>
                            </Col>
                        </Row>
                    </Card>
                </Col>
            </Row>

            {/* ═══════════ 营收趋势 / 畅销套餐 ═══════════ */}
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                <Col span={12}>
                    <Card
                        title="营收趋势"
                        extra={(
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ color: '#606266', fontSize: 13 }}>时间范围</span>
                                <Select value={timeRange} onChange={setTimeRange} options={timeRangeOptions} style={{ width: 120 }} />
                            </span>
                        )}
                        bordered={false}
                        style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}
                    >
                        <div ref={trend2Ref} style={{ width: '100%', height: 300 }} />
                    </Card>
                </Col>
                <Col span={12}>
                    <Card
                        title={(
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                <span>畅销套餐</span>
                                <Select value={hotPackageRefundMode} onChange={setHotPackageRefundMode} options={refundModeOptions} style={{ width: 120 }} />
                            </div>
                        )}
                        extra={(
                            <Segmented
                                value={region}
                                onChange={setRegion}
                                options={[
                                    { label: '国内', value: 'domestic' },
                                    { label: '海外', value: 'overseas' },
                                ]}
                            />
                        )}
                        bordered={false}
                        style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}
                    >
                        <Table columns={hotPackageColumns} dataSource={hotPackageData} rowKey="key" pagination={false} size="small" style={{ whiteSpace: 'nowrap' }} />
                    </Card>
                </Col>
            </Row>

            {/* ═══════════ 客户价值排名 / 明星产品 ═══════════ */}
            <Row gutter={[16, 16]}>
                <Col span={12}>
                    <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                            <span style={{ fontWeight: 600, fontSize: 15 }}>客户价值排名</span>
                            <span style={{ width: 1, height: 16, background: '#d9d9d9' }} />
                            <span style={{ color: '#606266', fontSize: 13 }}>排名方式</span>
                            <Select value={custSortKey} onChange={setCustSortKey} options={rankOptions} style={{ width: 140 }} />
                        </div>
                        <Table columns={custColumns} dataSource={custData} rowKey="key" pagination={false} components={tableHeaderNowrap} />
                    </Card>
                </Col>
                <Col span={12}>
                    <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                            <span style={{ fontWeight: 600, fontSize: 15 }}>明星产品</span>
                            <span style={{ width: 1, height: 16, background: '#d9d9d9' }} />
                            <span style={{ color: '#606266', fontSize: 13 }}>排名方式</span>
                            <Select value={starSortKey} onChange={setStarSortKey} options={rankOptions} style={{ width: 140 }} />
                        </div>
                        <Table
                            columns={starColumns}
                            dataSource={starData}
                            rowKey="key"
                            pagination={false}
                            components={tableHeaderNowrap}
                        />
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default OrderDashboard;
